import { CollisionPriority } from '@dnd-kit/abstract';
import { DragDropProvider, DragOverlay, PointerSensor } from '@dnd-kit/react';
import { cn } from '@repo/lib/cn';
import { GripVerticalIcon } from 'lucide-react';

import { QuestionMarkedTranslate } from '@/components/features/quiz/question/question-marked/question-translate';
import { QuestionBase, QuestionTranslatePlaceholder } from '@/components/features/quiz/question/question-resolver';
import { Badge } from '@/components/ui/badge';
import { DroppableArea, SortableItem } from '@/components/ui/dnd';
import { DraggableItem } from '@/components/ui/dnd/DraggableItem';
import { useQuestionTranslate } from '@/hooks/questions';

import type { QuestionTranslateProps } from '.';

export function QuestionTranslate(questionTranslateProps: QuestionTranslateProps) {
  const { question, showMarked } = questionTranslateProps;

  const { marked, availableOptions, selectedOptions, handleDragEnd, addOptionToSelected, removeOptionFromSelected, exportedUserAnswer } = useQuestionTranslate(questionTranslateProps);

  return (
    <QuestionBase content={question.content} type={question.type}>
      <DragDropProvider
        sensors={[PointerSensor]}
        onDragEnd={(event) => {
          const { target, source } = event.operation;
          const { canceled } = event;
          if (canceled) return;
          handleDragEnd(target, source);
        }}
      >
        <DroppableArea
          droppableProps={{
            id: 'answer-droppable-box',
            collisionPriority: CollisionPriority.Low,
            disabled: showMarked,
          }}
          className="border-border-secondary/25 min-h-18 rounded-md border-2 p-2"
          isOverClassName="bg-surface-primary-hover/45 duration-100 transition-colors"
        >
          {!showMarked && selectedOptions.length === 0 ? (
            <QuestionTranslatePlaceholder />
          ) : (
            <div className="flex h-full min-h-34 w-full grow flex-wrap gap-2">
              {selectedOptions.map((option, index) => (
                <SortableItem
                  key={option.id}
                  id={option.id}
                  index={index}
                  data={option}
                  args={{
                    disabled: showMarked,
                  }}
                  onClick={() => (showMarked ? undefined : removeOptionFromSelected(option))}
                >
                  <Badge variant="muted" size="md">
                    <GripVerticalIcon className={cn(showMarked && 'hidden')} />
                    {option.content}
                  </Badge>
                </SortableItem>
              ))}
            </div>
          )}
        </DroppableArea>

        <DroppableArea
          droppableProps={{
            id: 'dataset-droppable-box',
            collisionPriority: CollisionPriority.Low,
            disabled: showMarked,
          }}
          className="min-h-18 rounded-md p-2"
          isOverClassName="bg-surface-primary-hover/45 rounded-md duration-100 transition-colors"
        >
          <div className="flex h-full w-full grow flex-wrap gap-2">
            {availableOptions.map((option) => (
              <DraggableItem
                key={option.id}
                id={option.id}
                data={option}
                onClick={() => (showMarked ? undefined : addOptionToSelected(option))}
                args={{
                  disabled: showMarked,
                }}
              >
                <Badge variant="muted" size="md">
                  <GripVerticalIcon className={cn(showMarked && 'hidden')} />
                  {option.content}
                </Badge>
              </DraggableItem>
            ))}
          </div>
        </DroppableArea>

        <DragOverlay disabled={showMarked}>
          {(source) => (
            <Badge variant="muted" size="md" className="cursor-grabbing shadow-lg select-none">
              <GripVerticalIcon />
              {source.data.content}
            </Badge>
          )}
        </DragOverlay>
      </DragDropProvider>
      <QuestionMarkedTranslate question={question} marked={marked} answer={exportedUserAnswer} showMarked={showMarked} showQuestion={true} />
    </QuestionBase>
  );
}
